import * as Api from '@ViewModels';
import React from 'react';
import {
	MultipleSelectContent,
	MultipleSelectItem,
	MultipleSelectTrigger,
	MultipleSelectValue,
} from '../../../../../../../../web/components/MultipleSelect';
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../../../web/components/Select';
import {
	type ComboboxItem,
	EditModeCombobox,
	EditModeMultipleSelect,
	EditModeSelect,
	EditModeToggle,
} from '../context';
import { styleSheet } from '../styles';
import { SettingGroup } from './SettingGroup';
import { SettingRow } from './SettingRow';

interface ITagSettingsGroupProps {
	tagSettings: Api.ITagSettings;
	configuration: Api.IIntegrationConfiguration;
	provider: Api.IntegrationProvider;
	setTagsSettings: (settings: Api.ITagSettings) => void;
}

enum TagSettingType {
	State = 'State',
	City = 'City',
	PostalCode = 'PostalCode',
	Country = 'Country',
}

enum MyCaseTagSettingType {
	GroupPracticeArea = 'GroupPracticeArea',
	PeopleGroup = 'PeopleGroup',
	CaseNumber = 'CaseNumber',
	CaseName = 'CaseName',
}

enum ServiceTitanTagSettingType {
	DoNotMail = 'DoNotMail',
	DoNotService = 'DoNotService',
	CustomerType = 'CustomerType',
	BusinessUnit = 'BusinessUnit',
	MembershipStatus = 'MembershipStatus',
	MembershipFollowUpStatus = 'MembershipFollowUpStatus',
	MembershipTypeName = 'MembershipTypeName',
	ServiceAgreementName = 'ServiceAgreementName',
	ServiceAgreementStatus = 'ServiceAgreementStatus',
}

enum QuickBooksOnlineTagSettingType {
	Overdue = 'Overdue',
	LastInvoice = 'LastInvoice',
	ContactType = 'ContactType',
	Active = 'Active',
}

enum SalesforceCampaignTagSettingType {
	Name = 'Name',
}

const attorneyTagOptions = [
	{ label: 'None', value: Api.AttorneyTagOption.None },
	{ label: 'Most Recent', value: Api.AttorneyTagOption.MostRecent },
	{ label: 'All', value: Api.AttorneyTagOption.All },
];

type TagSettings =
	| Partial<Api.ITagSettings>
	| Partial<Api.IPracticePantherTagSettings>
	| Partial<Api.IMicrosoftDynamics365SalesTagSettings>
	| Partial<Api.IMyCaseTagSettings>
	| Partial<Api.IServiceTitanTagSettings>
	| Partial<Api.ISalesforceTagSettings>
	| Partial<Api.IQuickBooksOnlineTagSettings>;

export const TagSettingsGroup: React.FC<ITagSettingsGroupProps> = ({
	tagSettings,
	configuration,
	provider,
	setTagsSettings,
}) => {
	const isPracticePantherSettings = provider === Api.IntegrationProvider.PracticePanther;
	const ppTagSettings = isPracticePantherSettings ? (tagSettings as Api.IPracticePantherTagSettings) : null;

	const isMicrosoftDynamics365SalesSettings = provider === Api.IntegrationProvider.MicrosoftDynamics365Sales;
	const microsoftDynamics365SalesTagSettings = isMicrosoftDynamics365SalesSettings
		? (tagSettings as Api.IMicrosoftDynamics365SalesTagSettings)
		: null;

	const isMyCaseSettings = provider === Api.IntegrationProvider.MyCase;
	const myCaseTagSettings = isMyCaseSettings ? (tagSettings as Api.IMyCaseTagSettings) : null;

	const isServiceTitanSettings = provider === Api.IntegrationProvider.ServiceTitan;
	const serviceTitanTagSettings = isServiceTitanSettings ? (tagSettings as Api.IServiceTitanTagSettings) : null;

	const isSalesforceSettings = provider === Api.IntegrationProvider.Salesforce;
	const salesforceTagSettings = isSalesforceSettings ? (tagSettings as Api.ISalesforceTagSettings) : null;
	const salesforceConfiguration = isSalesforceSettings
		? (configuration as Api.ISalesforceIntegrationConfiguration)
		: null;
	const allAvailableSalesforceFields = {
		...salesforceConfiguration?.availableFields,
		...salesforceConfiguration?.availableCustomFields,
		...salesforceConfiguration?.availableDateFields,
		...salesforceConfiguration?.availableCustomDateFields,
	};
	const unsubscribeIndicatorOptions: { label: string; value: string }[] = [];
	Object.entries(allAvailableSalesforceFields ?? {}).forEach(([id, text]) => {
		unsubscribeIndicatorOptions.push({
			label: text,
			value: id,
		});
	});

	const isQBOSettings = provider === Api.IntegrationProvider.QuickBooksOnline;
	const qboTagSettings = isQBOSettings ? (tagSettings as Api.IQuickBooksOnlineTagSettings) : null;

	// Helper to get enabled tags as array
	const getEnabledTags = (): string[] => {
		const enabledTags: string[] = [];
		if (!tagSettings) return enabledTags;
		if (tagSettings.stateEnabled) enabledTags.push(TagSettingType.State);
		if (tagSettings.cityEnabled) enabledTags.push(TagSettingType.City);
		if (tagSettings.postalCodeEnabled) enabledTags.push(TagSettingType.PostalCode);
		if (tagSettings.countryEnabled) enabledTags.push(TagSettingType.Country);
		if (isMyCaseSettings) {
			if (myCaseTagSettings?.groupPracticeAreaTagEnabled) enabledTags.push(MyCaseTagSettingType.GroupPracticeArea);
			if (myCaseTagSettings?.peopleGroupTagEnabled) enabledTags.push(MyCaseTagSettingType.PeopleGroup);
			if (myCaseTagSettings?.caseNumberTagEnabled) enabledTags.push(MyCaseTagSettingType.CaseNumber);
			if (myCaseTagSettings?.caseNameTagEnabled) enabledTags.push(MyCaseTagSettingType.CaseName);
		}

		if (isServiceTitanSettings) {
			if (serviceTitanTagSettings?.doNotMailTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.DoNotMail);
			}

			if (serviceTitanTagSettings?.doNotServiceTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.DoNotService);
			}

			if (serviceTitanTagSettings?.customerTypeTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.CustomerType);
			}

			if (serviceTitanTagSettings?.businessUnitTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.BusinessUnit);
			}

			if (serviceTitanTagSettings?.membershipStatusTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.MembershipStatus);
			}

			if (serviceTitanTagSettings?.membershipFollowUpStatusTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.MembershipFollowUpStatus);
			}

			if (serviceTitanTagSettings?.membershipTypeNameTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.MembershipTypeName);
			}

			if (serviceTitanTagSettings?.serviceAgreementNameTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.ServiceAgreementName);
			}

			if (serviceTitanTagSettings?.serviceAgreementStatusTagEnabled) {
				enabledTags.push(ServiceTitanTagSettingType.ServiceAgreementStatus);
			}
		}

		if (isSalesforceSettings && salesforceTagSettings) {
			(salesforceTagSettings.enabledFields || []).forEach(fieldId => {
				enabledTags.push(fieldId);
			});
		}

		if (isQBOSettings && qboTagSettings) {
			if (qboTagSettings?.overdueTagEnabled) enabledTags.push(QuickBooksOnlineTagSettingType.Overdue);
			if (qboTagSettings?.lastInvoiceTagEnabled) enabledTags.push(QuickBooksOnlineTagSettingType.LastInvoice);
			if (qboTagSettings?.contactTypeTagEnabled) enabledTags.push(QuickBooksOnlineTagSettingType.ContactType);
			if (qboTagSettings?.activeTagEnabled) enabledTags.push(QuickBooksOnlineTagSettingType.Active);
		}

		return enabledTags;
	};

	const onUpdate = async (updatedSettings: TagSettings) => {
		setTagsSettings({
			...tagSettings,
			...updatedSettings,
		});
	};

	// Handle tag selection changes
	const handleTagChange = (selectedTags: string[]) => {
		let updatedSettings: TagSettings = {
			stateEnabled: selectedTags.includes(TagSettingType.State),
			cityEnabled: selectedTags.includes(TagSettingType.City),
			postalCodeEnabled: selectedTags.includes(TagSettingType.PostalCode),
			countryEnabled: selectedTags.includes(TagSettingType.Country),
		};

		// Add MyCase specific tags if applicable
		if (isMyCaseSettings) {
			updatedSettings = {
				...updatedSettings,
				groupPracticeAreaTagEnabled: selectedTags.includes(MyCaseTagSettingType.GroupPracticeArea),
				peopleGroupTagEnabled: selectedTags.includes(MyCaseTagSettingType.PeopleGroup),
				caseNumberTagEnabled: selectedTags.includes(MyCaseTagSettingType.CaseNumber),
				caseNameTagEnabled: selectedTags.includes(MyCaseTagSettingType.CaseName),
			};
		}

		// Add ServiceTitan specific tags
		if (isServiceTitanSettings) {
			updatedSettings = {
				...updatedSettings,
				doNotMailTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.DoNotMail),
				doNotServiceTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.DoNotService),
				customerTypeTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.CustomerType),
				businessUnitTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.BusinessUnit),
				membershipStatusTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.MembershipStatus),
				membershipFollowUpStatusTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.MembershipFollowUpStatus),
				membershipTypeNameTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.MembershipTypeName),
				serviceAgreementNameTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.ServiceAgreementName),
				serviceAgreementStatusTagEnabled: selectedTags.includes(ServiceTitanTagSettingType.ServiceAgreementStatus),
			};
		}

		// Add Salesforce fields
		if (isSalesforceSettings) {
			const fieldNames = Object.keys(salesforceConfiguration.availableFields || {});
			const enabledFields = selectedTags.filter(tag => fieldNames.includes(tag));

			updatedSettings = {
				...updatedSettings,
				enabledFields,
			};
		}

		// Add QuickBooksOnline specific tags
		if (isQBOSettings) {
			updatedSettings = {
				...updatedSettings,
				overdueTagEnabled: selectedTags.includes(QuickBooksOnlineTagSettingType.Overdue),
				lastInvoiceTagEnabled: selectedTags.includes(QuickBooksOnlineTagSettingType.LastInvoice),
				contactTypeTagEnabled: selectedTags.includes(QuickBooksOnlineTagSettingType.ContactType),
				activeTagEnabled: selectedTags.includes(QuickBooksOnlineTagSettingType.Active),
			};
		}

		onUpdate(updatedSettings);
	};

	const handleCustomFieldChange = (selectedCustomFields: string[]) => {
		onUpdate({ enabledCustomFields: selectedCustomFields });
	};

	const handleAccountTagsChange = (checked: boolean) => {
		onUpdate({ accountTagsEnabled: checked });
	};

	const handleAssignedUsersTagsChange = (checked: boolean) => {
		onUpdate({ assignedUsersTagsEnabled: checked });
	};

	const handleLeadAttorneyTagChange = (value: Api.AttorneyTagOption) => {
		onUpdate({ leadAttorneyTag: value });
	};

	const handleOriginatingAttorneyTagChange = (value: Api.AttorneyTagOption) => {
		onUpdate({ originatingAttorneyTag: value });
	};

	// Salesforce Campaign Tag helpers
	const getEnabledCampaignTags = (): string[] => {
		const enabledTags: string[] = [];
		if (salesforceTagSettings?.campaignTagSettings?.nameEnabled) {
			enabledTags.push(SalesforceCampaignTagSettingType.Name);
		}
		return enabledTags;
	};

	const handleCampaignTagChange = (selectedTags: string[]) => {
		onUpdate({
			campaignTagSettings: {
				...salesforceTagSettings?.campaignTagSettings,
				nameEnabled: selectedTags.includes(SalesforceCampaignTagSettingType.Name),
			},
		});
	};

	const getCampaignTagsDisplay = () => {
		const tags = getEnabledCampaignTags();
		if (!tags.length) return 'None selected';
		return tags.join(', ');
	};

	const getRemoteFieldDisplayName = (fieldId: string, availableFields: Record<string, string>) => {
		return availableFields?.[fieldId] || fieldId;
	};

	const getRemoteFieldsDisplayText = (
		fieldIds: string[] = [],
		availableFields: Record<string, string> = configuration.availableCustomFields
	) => {
		return fieldIds.map(fieldId => getRemoteFieldDisplayName(fieldId, availableFields));
	};

	const getRemoteFieldsLabel = (
		fieldIds: string[] = [],
		availableFields: Record<string, string> = configuration.availableCustomFields
	) => {
		const displayText = getRemoteFieldsDisplayText(fieldIds, availableFields);
		if (!displayText?.length) return 'None selected';
		return displayText.join(', ');
	};

	const getAttorneyTagOptionDisplay = (option: Api.AttorneyTagOption | null) => {
		if (!option) return 'None';
		switch (option) {
			case Api.AttorneyTagOption.None:
				return 'None';
			case Api.AttorneyTagOption.MostRecent:
				return 'Most Recent';
			case Api.AttorneyTagOption.All:
				return 'All';
			default:
				return 'Unknown';
		}
	};

	// Helper to convert Record<string, string> to ComboboxItem[]
	const recordToItems = (record: Record<string, string> = {}): ComboboxItem[] => {
		return Object.entries(record).map(([id, label]) => ({ id, label }));
	};

	// Get tag options based on provider
	const getTagOptions = (): ComboboxItem[] => {
		const options: ComboboxItem[] = [];

		// Location tags (not for Salesforce)
		if (provider !== Api.IntegrationProvider.Salesforce) {
			options.push(
				{ id: TagSettingType.State, label: 'State' },
				{ id: TagSettingType.City, label: 'City' },
				{ id: TagSettingType.PostalCode, label: 'Postal Code' },
				{ id: TagSettingType.Country, label: 'Country' }
			);
		}

		// MyCase specific
		if (isMyCaseSettings) {
			options.push(
				{ id: MyCaseTagSettingType.GroupPracticeArea, label: 'Group Practice Area' },
				{ id: MyCaseTagSettingType.PeopleGroup, label: 'People Group' },
				{ id: MyCaseTagSettingType.CaseNumber, label: 'Case Number' },
				{ id: MyCaseTagSettingType.CaseName, label: 'Case Name' }
			);
		}

		// ServiceTitan specific
		if (isServiceTitanSettings) {
			options.push(
				{ id: ServiceTitanTagSettingType.DoNotMail, label: 'Do Not Mail' },
				{ id: ServiceTitanTagSettingType.DoNotService, label: 'Do Not Service' },
				{ id: ServiceTitanTagSettingType.CustomerType, label: 'Customer Type' },
				{ id: ServiceTitanTagSettingType.BusinessUnit, label: 'Business Unit' },
				{ id: ServiceTitanTagSettingType.MembershipStatus, label: 'Membership Status' },
				{ id: ServiceTitanTagSettingType.MembershipFollowUpStatus, label: 'Membership Follow-Up Status' },
				{ id: ServiceTitanTagSettingType.MembershipTypeName, label: 'Membership Type Name' },
				{ id: ServiceTitanTagSettingType.ServiceAgreementName, label: 'Service Agreement Name' },
				{ id: ServiceTitanTagSettingType.ServiceAgreementStatus, label: 'Service Agreement Status' }
			);
		}

		// Salesforce fields
		if (isSalesforceSettings && salesforceConfiguration) {
			options.push(...recordToItems(salesforceConfiguration.availableFields));
		}

		// QuickBooks Online specific
		if (isQBOSettings) {
			options.push(
				{ id: QuickBooksOnlineTagSettingType.Overdue, label: 'Overdue' },
				{ id: QuickBooksOnlineTagSettingType.LastInvoice, label: 'Last Invoice' },
				{ id: QuickBooksOnlineTagSettingType.ContactType, label: 'Contact Type' },
				{ id: QuickBooksOnlineTagSettingType.Active, label: 'Active' }
			);
		}

		return options;
	};

	// Get display value for enabled tags
	const getTagOptionsDisplay = (): string => {
		const enabledTags = getEnabledTags();
		if (!enabledTags.length) return 'None selected';

		const tagOptions = getTagOptions();
		return enabledTags.map(tagId => tagOptions.find(opt => opt.id === tagId)?.label || tagId).join(', ');
	};

	return (
		<SettingGroup title='Tag Settings'>
			<SettingRow title='Tags' subtitle='Select taggable information to associate with contacts'>
				<EditModeCombobox
					multiple
					items={getTagOptions()}
					value={getEnabledTags()}
					onChange={handleTagChange}
					displayValue={getTagOptionsDisplay()}
					searchPlaceholder='Search tags...'
				/>
			</SettingRow>

			{/* Provider-specific settings */}
			{isPracticePantherSettings && ppTagSettings && (
				<>
					<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
						<EditModeCombobox
							multiple
							items={recordToItems(configuration.availableCustomFields)}
							value={ppTagSettings.enabledCustomFields || []}
							onChange={handleCustomFieldChange}
							displayValue={getRemoteFieldsLabel(ppTagSettings.enabledCustomFields)}
							searchPlaceholder='Search custom fields...'
						/>
					</SettingRow>

					<SettingRow
						title='Account Tags'
						subtitle='Enable automatic tagging of contacts based on their associated accounts'
					>
						<EditModeToggle
							id='accountTags'
							isOn={ppTagSettings.accountTagsEnabled}
							onToggleCheckChanged={handleAccountTagsChange}
						/>
					</SettingRow>

					<SettingRow
						title='Assigned Users Tags'
						subtitle='Enable automatic tagging of contacts based on assigned users'
					>
						<EditModeToggle
							id='assignedUsersTags'
							isOn={ppTagSettings.assignedUsersTagsEnabled}
							onToggleCheckChanged={handleAssignedUsersTagsChange}
						/>
					</SettingRow>
				</>
			)}
			{isMicrosoftDynamics365SalesSettings && microsoftDynamics365SalesTagSettings && (
				<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
					<EditModeCombobox
						multiple
						items={recordToItems(configuration.availableCustomFields)}
						value={microsoftDynamics365SalesTagSettings.enabledCustomFields || []}
						onChange={handleCustomFieldChange}
						displayValue={getRemoteFieldsLabel(microsoftDynamics365SalesTagSettings.enabledCustomFields)}
						searchPlaceholder='Search custom fields...'
					/>
				</SettingRow>
			)}
			{isMyCaseSettings && myCaseTagSettings && (
				<>
					<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
						<EditModeCombobox
							multiple
							items={recordToItems(configuration.availableCustomFields)}
							value={myCaseTagSettings.enabledCustomFields || []}
							onChange={handleCustomFieldChange}
							displayValue={getRemoteFieldsLabel(myCaseTagSettings.enabledCustomFields)}
							searchPlaceholder='Search custom fields...'
						/>
					</SettingRow>
					<SettingRow
						title='Lead Attorney Tag Options'
						subtitle='Control how lead attorney information is tagged on contacts'
					>
						<EditModeSelect
							items={attorneyTagOptions}
							value={myCaseTagSettings?.leadAttorneyTag ?? null}
							onValueChange={handleLeadAttorneyTagChange}
							displayValue={getAttorneyTagOptionDisplay(myCaseTagSettings?.leadAttorneyTag) || null}
						>
							<SelectTrigger styles={styleSheet.editableField}>
								<SelectValue placeholder='Select lead attorney tag options' />
							</SelectTrigger>
							<SelectContent>
								{attorneyTagOptions.map(o => (
									<SelectItem key={o.value} value={o.value}>
										{o.label}
									</SelectItem>
								))}
							</SelectContent>
						</EditModeSelect>
					</SettingRow>
					<SettingRow
						title='Originating Attorney Tag Options'
						subtitle='Control how originating attorney information is tagged on contacts'
					>
						<EditModeSelect
							items={attorneyTagOptions}
							value={myCaseTagSettings?.originatingAttorneyTag ?? null}
							onValueChange={handleOriginatingAttorneyTagChange}
							displayValue={getAttorneyTagOptionDisplay(myCaseTagSettings?.originatingAttorneyTag) || null}
						>
							<SelectTrigger styles={styleSheet.editableField}>
								<SelectValue placeholder='Select originating attorney tag options' />
							</SelectTrigger>
							<SelectContent>
								{attorneyTagOptions.map(o => (
									<SelectItem key={o.value} value={o.value}>
										{o.label}
									</SelectItem>
								))}
							</SelectContent>
						</EditModeSelect>
					</SettingRow>
				</>
			)}
			{isServiceTitanSettings && serviceTitanTagSettings && (
				<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
					<EditModeCombobox
						multiple
						items={recordToItems(configuration.availableCustomFields)}
						value={serviceTitanTagSettings.enabledCustomFields || []}
						onChange={handleCustomFieldChange}
						displayValue={getRemoteFieldsLabel(serviceTitanTagSettings.enabledCustomFields)}
						searchPlaceholder='Search custom fields...'
					/>
				</SettingRow>
			)}
			{isSalesforceSettings && salesforceTagSettings ? (
				<>
					<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
						<EditModeCombobox
							multiple
							items={recordToItems(configuration.availableCustomFields)}
							value={salesforceTagSettings.enabledCustomFields || []}
							onChange={handleCustomFieldChange}
							displayValue={getRemoteFieldsLabel(salesforceTagSettings.enabledCustomFields)}
							searchPlaceholder='Search custom fields...'
						/>
					</SettingRow>
					<SettingRow title='Unsubscribe Indicators' subtitle='Maps Salesforce fields to mark contacts as unsubscribed'>
						<EditModeCombobox
							multiple
							items={recordToItems(allAvailableSalesforceFields)}
							value={salesforceTagSettings?.unsubscribeFields || []}
							onChange={(selectedFields: string[]) => {
								onUpdate({ unsubscribeFields: selectedFields });
							}}
							displayValue={getRemoteFieldsLabel(
								salesforceTagSettings?.unsubscribeFields,
								allAvailableSalesforceFields
							)}
							searchPlaceholder='Search fields...'
						/>
					</SettingRow>
					<SettingRow title='Campaign Tags' subtitle='Tag contacts based on their Salesforce Campaign membership'>
						<EditModeMultipleSelect
							value={getEnabledCampaignTags()}
							onChange={handleCampaignTagChange}
							displayValue={getCampaignTagsDisplay()}
						>
							<MultipleSelectTrigger styles={styleSheet.editableField}>
								<MultipleSelectValue values={getEnabledCampaignTags()} placeholder='Select campaign tag types' />
							</MultipleSelectTrigger>
							<MultipleSelectContent>
								<MultipleSelectItem
									value={SalesforceCampaignTagSettingType.Name}
									selected={salesforceTagSettings?.campaignTagSettings?.nameEnabled}
								>
									Name
								</MultipleSelectItem>
							</MultipleSelectContent>
						</EditModeMultipleSelect>
					</SettingRow>
				</>
			) : null}
		</SettingGroup>
	);
};
